import { useEffect, useMemo, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Package, ArrowRight, Loader2, ShoppingCart, Clock, Check, Send,
} from "lucide-react";

const REORDER_STAGES = [
  "request_received", "production_invoice_sent", "production_paid",
  "production_scheduled", "in_production", "quality_review",
  "shipping_invoice_sent", "shipping_paid", "shipped", "delivered", "closed",
];

const PRODUCTION_STAGES = [
  { k: "design", l: "Design" },
  { k: "artwork_submitted", l: "Artwork Submitted" },
  { k: "proofing", l: "Proofing" },
  { k: "client_approval", l: "Client Approval" },
  { k: "plate_production", l: "Plate Production" },
  { k: "printing", l: "Printing" },
  { k: "filling", l: "Filling" },
  { k: "quality_control", l: "Quality Control" },
  { k: "production_complete", l: "Production Complete" },
  { k: "shipping_scheduled", l: "Shipping Scheduled" },
  { k: "shipped", l: "Shipped" },
  { k: "delivered", l: "Delivered" },
];

const PALLETS = [
  { value: "1", label: "1 Pallet" },
  { value: "2", label: "2 Pallets" },
  { value: "3", label: "3 Pallets" },
  { value: "4", label: "4 Pallets" },
  { value: "5+", label: "5+ Pallets (We'll Contact You)" },
];

const PortalReorder = () => {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [reorders, setReorders] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/portal/turnkey-orders").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([])),
      api.get("/portal/turnkey-reorders").then((r) => { setReorders(r.data.reorders || []); setLabels(r.data.pipeline_labels || {}); }).catch(() => setReorders([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <PortalLayout>
      <div className="mb-6">
        <div className="ft-eyebrow mb-2">Client Portal</div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="portal-orders-title">
          Orders &amp; Reorders
        </h1>
        <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
          Your central hub for every FLOWTALIS order. Review your original turnkey purchase,
          production status, and submit new production runs — all from one place.
        </p>
      </div>

      <div className="flex gap-2 border-b border-[var(--ft-border)] mb-6" data-testid="portal-orders-tabs">
        {[
          { k: "orders", l: "My Orders", n: orders.length },
          { k: "reorder", l: "Reorder", n: null },
          { k: "history", l: "Reorder History", n: reorders.length },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.k
                ? "border-[var(--ft-gold)] text-[var(--ft-black)]"
                : "border-transparent text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
            }`}
            data-testid={`portal-tab-${t.k}`}
          >
            {t.l} {t.n !== null && <span className="text-[var(--ft-muted)] font-normal">({t.n})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : tab === "orders" ? (
        <MyOrdersTab orders={orders} onReorder={() => setTab("reorder")} />
      ) : tab === "reorder" ? (
        <ReorderTab orders={orders} onDone={() => { load(); setTab("history"); }} />
      ) : (
        <ReorderHistoryTab reorders={reorders} labels={labels} />
      )}
    </PortalLayout>
  );
};

// ------------------------------------------------------- My Orders
const MyOrdersTab = ({ orders, onReorder }) => {
  if (orders.length === 0) {
    return (
      <div className="ft-card text-center py-16" data-testid="portal-orders-empty">
        <Package size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
        <h3 className="text-lg font-semibold">No orders yet.</h3>
        <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
          Your turnkey brand purchases will appear here once production begins. If you've recently
          submitted an inquiry, it will show up here as soon as our team confirms your order.
        </p>
      </div>
    );
  }
  return (
    <div className="grid gap-4" data-testid="portal-orders-list">
      {orders.map((o) => (
        <div key={o.id} className="ft-card" data-testid={`portal-order-${o.id}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="ft-eyebrow mb-1">Turnkey Brand Purchase</div>
              <h3 className="text-xl font-bold tracking-tight">{o.brand_name}</h3>
              {o.design_edition_label && <div className="text-xs text-[var(--ft-muted)] mt-1">{o.design_edition_label}</div>}
            </div>
            <div className="text-right">
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">Status</div>
              <div className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}>
                {o.status_label || o.status || "—"}
              </div>
            </div>
          </div>
          <div className="ft-gold-rule my-4" />
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <KV label="Beverage" value={o.brand_default_beverage || o.preferred_beverage_label} />
            <KV label="Package" value={(o.package_label || "").split("—")[0].trim()} />
            <KV label="Purchase Date" value={o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"} />
            <KV label="Timeframe" value={o.timeframe_label} />
          </div>
          {o.activity && o.activity.length > 0 && (
            <div className="mt-5 pt-4 border-t border-[var(--ft-border)]">
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold mb-2">Order Timeline</div>
              <ul className="space-y-1.5">
                {[...o.activity].filter((a) => !a.internal).reverse().slice(0, 3).map((a, i) => (
                  <li key={i} className="flex gap-2 text-xs">
                    <Clock size={10} className="mt-1 text-[var(--ft-gold)] shrink-0" />
                    <span className="text-[var(--ft-muted)]">{new Date(a.at).toLocaleDateString()}</span>
                    <span className="text-[var(--ft-charcoal)]">{a.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Production Tracker — 12-stage visual timeline */}
          {(() => {
            const idx = PRODUCTION_STAGES.findIndex((s) => s.k === o.production_stage);
            if (idx < 0) return null;
            return (
              <div className="mt-5 pt-4 border-t border-[var(--ft-border)]" data-testid={`portal-production-${o.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[var(--ft-gold)] font-semibold">Production Tracker</div>
                  <div className="text-xs text-[var(--ft-charcoal)] font-semibold">
                    Stage {idx + 1} of {PRODUCTION_STAGES.length}: {PRODUCTION_STAGES[idx].l}
                  </div>
                </div>
                <div className="flex gap-1">
                  {PRODUCTION_STAGES.map((s, i) => (
                    <div key={s.k} className="flex-1 h-1.5 rounded-full" style={{ background: i <= idx ? "var(--ft-gold)" : "var(--ft-border)" }} title={s.l} />
                  ))}
                </div>
                {o.estimated_completion && (
                  <div className="text-[0.7rem] text-[var(--ft-muted)] mt-2">
                    Estimated completion: <span className="font-semibold text-[var(--ft-charcoal)]">{new Date(o.estimated_completion).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="mt-5 flex justify-end">
            <button onClick={onReorder} className="ft-btn-secondary" data-testid={`portal-order-reorder-${o.id}`}>
              <ShoppingCart size={14} /> Reorder This Brand <ArrowRight size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------- Reorder form
const ReorderTab = ({ orders, onDone }) => {
  const eligibleOrder = orders[0]; // most recent order autofills the form
  const [form, setForm] = useState({
    brand_slug: eligibleOrder?.brand_slug || "",
    brand_name: eligibleOrder?.brand_name || "",
    beverage_type: eligibleOrder?.brand_default_beverage || "",
    pallets: "1",
    ready_to_order: "yes",
    additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (ready) => {
    if (!form.brand_slug) return toast.error("No eligible brand — your original order must be in production first.");
    setSubmitting(true);
    try {
      await api.post("/portal/turnkey-reorder", { ...form, ready_to_order: ready });
      setSubmitted(true);
      toast.success(ready === "yes" ? "Reorder request submitted." : "Draft saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (!eligibleOrder) {
    return (
      <div className="ft-card text-center py-16" data-testid="portal-reorder-empty">
        <Package size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
        <h3 className="text-lg font-semibold">Reorder unlocks once your first order is in production.</h3>
        <p className="text-[var(--ft-muted)] mt-2 max-w-md mx-auto">
          Once your original turnkey brand purchase reaches the production stage, you can submit
          pallet-based reorders from this page.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="ft-card" data-testid="portal-reorder-success">
        <div className="h-14 w-14 rounded-full flex items-center justify-center mb-5" style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}>
          <Check size={26} strokeWidth={2} />
        </div>
        <div className="ft-eyebrow mb-2">Reorder Received</div>
        <h3 className="text-2xl font-bold tracking-tight mb-3">Thank you.</h3>
        <p className="text-[var(--ft-muted)] leading-relaxed max-w-lg">
          Your reorder request has been received. Our team will review and send you two separate
          invoices: a <strong>Production Invoice</strong> based on the pallets requested, and a
          <strong> Shipping Invoice</strong> once your delivery destination is confirmed.
          Production begins once payment has been received.
        </p>
        <button onClick={onDone} className="ft-btn-primary mt-6" data-testid="portal-reorder-view-history">View Reorder History <ArrowRight size={14} /></button>
      </div>
    );
  }

  return (
    <div className="ft-card space-y-5" data-testid="portal-reorder-form">
      <div>
        <div className="ft-eyebrow mb-2">Submit a Reorder</div>
        <p className="text-sm text-[var(--ft-muted)] leading-relaxed">
          Ready for your next production run? Submit a reorder request below and our team will
          prepare your production and shipping invoices. Once payment is received, we&apos;ll begin
          manufacturing your next order.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Brand Name (read-only)">
          <input className="ft-input" value={form.brand_name} disabled data-testid="portal-reorder-brand" />
        </Field>
        <Field label="Beverage Type (read-only)">
          <input className="ft-input" value={form.beverage_type || "—"} disabled data-testid="portal-reorder-beverage" />
        </Field>
      </div>

      <Field label="Number of Pallets *">
        <select className="ft-input" value={form.pallets} onChange={(e) => setForm((f) => ({ ...f, pallets: e.target.value }))} data-testid="portal-reorder-pallets">
          {PALLETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </Field>

      <Field label="Additional Notes">
        <textarea
          className="ft-input min-h-[90px]"
          value={form.additional_notes}
          onChange={(e) => setForm((f) => ({ ...f, additional_notes: e.target.value }))}
          placeholder="New shipping address, delivery instructions, requested production timeline, questions…"
          data-testid="portal-reorder-notes"
        />
      </Field>

      <div className="p-4 rounded-lg bg-[var(--ft-gold-soft)] text-xs text-[var(--ft-charcoal)] leading-relaxed">
        <strong>Reminder:</strong> No online payment is processed here. Production and shipping are
        invoiced separately by the FLOWTALIS team. Production begins after the required invoice(s)
        have been paid.
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <button onClick={() => submit("not_yet")} disabled={submitting} className="ft-btn-ghost" data-testid="portal-reorder-draft">
          Save as Draft
        </button>
        <button onClick={() => submit("yes")} disabled={submitting} className="ft-btn-primary" data-testid="portal-reorder-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit Reorder Request
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------- Reorder history
const ReorderHistoryTab = ({ reorders, labels }) => {
  const stageColor = useMemo(() => ({
    request_received: "#c8a24a", production_invoice_sent: "#946600",
    production_paid: "#4a7c3e", production_scheduled: "#3d7f9c",
    in_production: "#2f5f3f", quality_review: "#5a5a5a",
    shipping_invoice_sent: "#946600", shipping_paid: "#4a7c3e",
    shipped: "#1a4a1a", delivered: "#1a4a1a", closed: "#666", draft: "#999",
  }), []);

  if (reorders.length === 0) {
    return (
      <div className="ft-card text-center py-16" data-testid="portal-reorders-empty">
        <ShoppingCart size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
        <h3 className="text-lg font-semibold">No reorders yet.</h3>
      </div>
    );
  }

  return (
    <div className="grid gap-4" data-testid="portal-reorders-list">
      {reorders.map((r) => {
        const idx = REORDER_STAGES.indexOf(r.status);
        return (
          <div key={r.id} className="ft-card" data-testid={`portal-reorder-${r.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="ft-eyebrow mb-1">{r.pallets} Pallet(s) · {r.brand_name}</div>
                <h3 className="text-lg font-bold tracking-tight">Reorder #{r.id.slice(0, 8).toUpperCase()}</h3>
                <div className="text-xs text-[var(--ft-muted)] mt-1">Submitted {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: stageColor[r.status] || "#666", color: "#fff" }}>
                {r.status_label || labels[r.status] || r.status}
              </div>
            </div>
            {idx >= 0 && (
              <div className="mt-5">
                <div className="flex gap-1">
                  {REORDER_STAGES.map((s, i) => (
                    <div key={s} className="flex-1 h-1.5 rounded-full" style={{ background: i <= idx ? "var(--ft-gold)" : "var(--ft-border)" }} />
                  ))}
                </div>
                <div className="text-[0.65rem] text-[var(--ft-muted)] mt-2">Stage {idx + 1} of {REORDER_STAGES.length}</div>
              </div>
            )}
            {r.additional_notes && (
              <div className="mt-4 pt-4 border-t border-[var(--ft-border)] text-sm text-[var(--ft-muted)]">
                <span className="font-semibold text-[var(--ft-charcoal)]">Your notes: </span>{r.additional_notes}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const KV = ({ label, value }) => (
  <div>
    <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">{label}</div>
    <div className="text-sm text-[var(--ft-charcoal)] mt-1">{value || "—"}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="ft-label block mb-1.5">{label}</label>
    {children}
  </div>
);

export default PortalReorder;
